import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Field, PasswordInput, PrimaryButton, FormAlert, inputCls } from "@/components/FormBits";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, maskPhone, phoneDigits } from "@/lib/api";

const LoggedInNotice = ({ user, logout }) => (
  <div data-testid="login-already-logged" className="space-y-5">
    <p className="text-lg text-slate-700">Você já está conectado como <strong>{user.name}</strong>.</p>
    <Link to={user.role === "admin" ? "/admin" : "/painel"} data-testid="login-go-dashboard-link" className="flex min-h-[60px] items-center justify-center rounded-2xl bg-emerald-700 font-heading text-xl font-bold text-white">
      {user.role === "admin" ? "Ir para o painel ADM" : "Ir para o meu painel"}
    </Link>
    <button onClick={logout} data-testid="login-logout-button" className="flex w-full min-h-[56px] items-center justify-center gap-2 rounded-2xl border-2 border-slate-300 text-lg font-bold text-slate-800 hover:bg-slate-50">
      <LogOut className="h-5 w-5" /> Sair da conta
    </button>
  </div>
);

export default function Login() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (phoneDigits(phone).length < 10 || !password) {
      setError("Digite seu celular com DDD e sua senha.");
      return;
    }
    setLoading(true);
    try {
      const u = await login(phoneDigits(phone), password);
      navigate("/painel");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testId="login-page" title="Entrar" subtitle="Use o celular e a senha que você cadastrou.">
      {user ? <LoggedInNotice user={user} logout={logout} /> : (
        <form onSubmit={submit} noValidate className="space-y-6" data-testid="login-form">
          <Field id="login-phone" label="Celular">
            <input id="login-phone" data-testid="login-phone-input" className={inputCls} value={phone} onChange={(e) => setPhone(maskPhone(e.target.value))} inputMode="numeric" autoComplete="tel" placeholder="(11) 98765-4321" />
          </Field>
          <Field id="login-password" label="Senha">
            <PasswordInput id="login-password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </Field>
          <div className="text-right">
            <Link to="/esqueci-senha" data-testid="login-forgot-password-link" className="text-lg font-semibold text-emerald-800 underline underline-offset-4">
              Esqueci minha senha
            </Link>
          </div>
          <FormAlert testId="login-error">{error}</FormAlert>
          <PrimaryButton type="submit" loading={loading} data-testid="login-submit-button">Entrar</PrimaryButton>
          <p className="text-center text-lg text-slate-700">
            Ainda não tem conta?{" "}
            <Link to="/cadastro" data-testid="login-go-signup-link" className="font-bold text-emerald-800 underline underline-offset-4">Criar minha conta</Link>
          </p>
          <Link
            to="/adm/login"
            data-testid="login-go-admin-link"
            className="flex min-h-[52px] items-center justify-center gap-2 rounded-xl border-2 border-amber-200 bg-amber-50 px-4 text-lg font-bold text-amber-950 transition-colors hover:bg-amber-100"
          >
            <ShieldCheck className="h-5 w-5" />
            Acesso ADM
          </Link>
        </form>
      )}
    </AuthShell>
  );
}
